import { useCallback, useState } from 'react';
import { IntlShape } from 'react-intl';

import { IEX_STATE_CODES } from '../../../constants/configs/states';

import { validateSupportDocument } from '../utils';
import { SUFFIXES } from '../constant';
import { PDF_TYPE_FILE, XML_TYPE_FILE } from '../../../constants/common/fileTypes';
import { NUMBER } from '../../../constants/common/number';

interface IUseLoadFileValidationProps {
    stateCode: string;
    carrier: string;
    intl: IntlShape;
}

const useLoadFileValidation = ({ stateCode, carrier, intl }: IUseLoadFileValidationProps) => {
    const [fileValidMessage, setFileValidMessage] = useState('');
    const [isInvalidFileList, setIsInvalidFileList] = useState(true);
    const [isValid, setIsValid] = useState(true);

    const validateFile = useCallback(
        files => {
            let fileErrors: string[] = [];
            let fileList = '';
            let cntMandatory = 0;

            const errorMsg = intl.formatMessage({ id: 'admin.loadPlans.errors.file.requiredFILE' });
            const hiosIssuerIdWithState = `${carrier}${stateCode}`;

            if (files?.length < NUMBER.FOUR) {
                fileErrors.push(intl.formatMessage({ id: 'admin.loadPlans.errors.file.requiredFILE' }));
            } else if (stateCode !== IEX_STATE_CODES.MN && files?.length > NUMBER.SEVEN) {
                fileErrors.push(intl.formatMessage({ id: 'admin.loadPlans.errors.file.wrongTemplate' }));
            } else {
                const allowedFileTypes =
                    stateCode === IEX_STATE_CODES.MN ? [XML_TYPE_FILE, PDF_TYPE_FILE] : [XML_TYPE_FILE];

                const errorMsgForAllowedFiles =
                    stateCode === IEX_STATE_CODES.MN
                        ? intl.formatMessage({ id: 'admin.loadPlans.errors.file.requiredFormat' })
                        : intl.formatMessage({ id: 'admin.loadPlans.errors.file.requiredXML' });

                for (let fi = 0; fi < files?.length; fi++) {
                    const file = files[fi];
                    if (!allowedFileTypes.includes(file.type)) {
                        fileErrors.push(errorMsgForAllowedFiles);
                        break;
                    }

                    const originalFileName = file.name;
                    const fileName = originalFileName.toLowerCase();

                    if (fileName.includes(SUFFIXES.NETWORK_SUFFIX)) {
                        cntMandatory += 1;
                        continue;
                    } else if (fileName.includes(SUFFIXES.BENEFITS_SUFFIX)) {
                        cntMandatory += 10;
                        continue;
                    } else if (fileName.includes(SUFFIXES.RATE_SUFFIX)) {
                        cntMandatory += 100;
                        continue;
                    } else if (fileName.includes(SUFFIXES.SERVICE_AREA_SUFFIX)) {
                        cntMandatory += 1000;
                        continue;
                    } else if (
                        fileName.includes(SUFFIXES.DRUG_SUFFIX) ||
                        fileName.includes(SUFFIXES.BUSINESS_RULE_SUFFIX) ||
                        fileName.includes(SUFFIXES.UNIFIED_RATE_SUFFIX)
                    ) {
                        continue;
                    } else {
                        if (
                            stateCode !== IEX_STATE_CODES.MN ||
                            file.type !== PDF_TYPE_FILE ||
                            !validateSupportDocument(originalFileName, hiosIssuerIdWithState)
                        ) {
                            fileList += `${originalFileName}, `;
                        }
                    }
                }

                if (cntMandatory !== 1111) {
                    fileErrors.push(errorMsg);
                }
            }
            

            if (fileList) {
                fileErrors.push(
                    intl.formatMessage(
                        {
                            id: 'admin.loadPlans.errors.file.wrongTemplate2',
                        },
                        { fileName: fileList.substring(0, fileList?.length - 2) },
                    ),
                );
            }

            let hasValidValue = true;
            if (fileErrors?.length > 0) {
                if (fileErrors?.length > 1) {
                    fileErrors = fileErrors.map(error => `-${error}`);
                }

                setFileValidMessage(fileErrors.join('<br />'));
                hasValidValue = false;
            } else {
                setFileValidMessage('');
            }

            setIsInvalidFileList(!hasValidValue);
            setIsValid(hasValidValue);

            return hasValidValue;
        },
        [carrier],
    );

    const resetIsFileValid = () => {
        setIsValid(true);
        setIsInvalidFileList(true);
        setFileValidMessage('');
    };

    return {
        fileValidMessage,
        isInvalidFileList,
        isValid,
        validateFile,
        resetIsFileValid,
    };
};

export default useLoadFileValidation;
